import { useState } from 'react'
import { useOverflowFocus } from '@/hooks/use-overflow-focus'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import {
  useAddBillingsRep,
  useBillingsAttribution,
  useSaveBillingsAlias,
  useSaveBillingsCategory,
  useSetBillingsCategoryActive,
  useSetBillingsRepActive,
  useUsers,
} from '@/hooks/use-api'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { explainError } from '@/lib/explain-error'

const CATEGORIES = [
  { value: 'software', label: 'Software' },
  { value: 'remediation', label: 'Remediation' },
  { value: 'other_services', label: 'Other services' },
  { value: 'unclassified', label: 'Leave uncategorised' },
]

/**
 * The attribution queue — what needs a person to decide.
 *
 * This replaces the hand-maintained "Corrected Sales Rep" column, and keeps apart
 * the two jobs that column conflated:
 *
 *   a rep mapping   what a SPELLING means. "Vanessa Madore4" is Vanessa Madore,
 *                   always. Deterministic, so it is safe to record once.
 *   a line override who owns THESE LINES. A judgement, made from the rep's page.
 *
 * Nothing is suggested or applied automatically. Similarity scoring was measured on
 * this kind of data and confidently produced wrong answers, and a rep attribution
 * carries someone's commission.
 */
export default function SalesTrackersAttribution() {
  const queue = useOverflowFocus('QuickBooks rep spellings and who they are attributed to')
  const accounts = useOverflowFocus('Sales accounts and their categories')
  const repList = useOverflowFocus('Reps and whether they appear in reporting')
  useDocumentTitle('Billings attribution')

  const { toast } = useToast()
  const attribution = useBillingsAttribution()
  const users = useUsers({ limit: 10000 })
  const saveAlias = useSaveBillingsAlias()
  const saveCategory = useSaveBillingsCategory()
  const setCategoryActive = useSetBillingsCategoryActive()
  const setRepActive = useSetBillingsRepActive()
  const addRep = useAddBillingsRep()

  const [repChoice, setRepChoice] = useState<Record<string, string>>({})
  const [catChoice, setCatChoice] = useState<Record<string, string>>({})
  const [error, setError] = useState('')
  const [announcement, setAnnouncement] = useState('')
  const [newRepName, setNewRepName] = useState('')
  const [newAccount, setNewAccount] = useState('')
  const [newAccountCategory, setNewAccountCategory] = useState('software')
  const [newAccountLabel, setNewAccountLabel] = useState('')
  const [labelEdits, setLabelEdits] = useState<Record<string, string>>({})
  const [newRepUser, setNewRepUser] = useState('')

  const money = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 0 })
  const data = attribution.data
  const userList = users.data?.data ?? []

  const handleSaveRep = async (rawValue: string) => {
    const choice = repChoice[rawValue]
    if (!choice) return
    setError('')
    try {
      // A choice may be a user id or a non-person bucket label.
      const isUser = choice.startsWith('005')
      const result = await saveAlias.mutateAsync({
        raw_value: rawValue,
        rep_user_id: isUser ? choice : null,
        rep_label: isUser ? undefined : choice,
      })
      void result
      toast({
        title: 'Rep mapping saved',
        description: `"${rawValue}" will now be attributed automatically.`,
      })
    } catch (err) {
      setError(explainError(err, 'Could not save that rep mapping.'))
    }
  }

  /**
   * `explicit` is passed by the management table, where changing the select IS the
   * decision, so there is no Save button to press. The queue below still uses the
   * two-step form, because there a choice is being made about something the CRM could
   * not place and a moment to change your mind is worth having.
   */
  const handleSaveCategory = async (salesAccount: string, explicit?: string) => {
    const choice = explicit ?? catChoice[salesAccount]
    if (!choice) return
    setError('')
    try {
      await saveCategory.mutateAsync({ sales_account: salesAccount, category: choice })
      toast({
        title: 'Category saved',
        description: `"${salesAccount}" now counts as ${choice.replace('_', ' ')}.`,
      })
    } catch (err) {
      setError(explainError(err, 'Could not save that category.'))
    }
  }

  const handleCategoryActive = async (salesAccount: string, isActive: boolean) => {
    setError('')
    try {
      await setCategoryActive.mutateAsync({ sales_account: salesAccount, is_active: isActive })
      // Said in full, because "retired" reads as "removed from the tracker" and it is
      // not: the historical lines keep counting.
      const what = isActive
        ? `"${salesAccount}" is active again and back in the lists.`
        : `"${salesAccount}" is retired. It is out of the lists, and its past sales still count in every total.`
      setAnnouncement(what)
      toast({ title: isActive ? 'Product reinstated' : 'Product retired', description: what })
    } catch (err) {
      setError(explainError(err, 'Could not change that sales account.'))
    }
  }

  const handleRepActive = async (repLabel: string, rosterId: number | null, isActive: boolean) => {
    setError('')
    try {
      await setRepActive.mutateAsync({
        roster_id: rosterId,
        rep_label: repLabel,
        is_active: isActive,
      })
      const what = isActive
        ? `${repLabel} is active again and back in the rep list.`
        : `${repLabel} is retired. Their past sales still count in every total.`
      setAnnouncement(what)
      toast({ title: isActive ? 'Rep reinstated' : 'Rep retired', description: what })
    } catch (err) {
      setError(explainError(err, 'Could not change that rep.'))
    }
  }

  /**
   * Change who a QuickBooks spelling maps to, or whether it is a house account.
   *
   * Jason's second review: "please add the ability to edit/update existing rep
   * mappings." The card was entirely read-only before, so a spelling attributed to the
   * wrong person could only be fixed by adding a second mapping.
   *
   * Reuses the existing upsertAlias, which keys on the raw value and already overwrites
   * the label from the linked user's name, so one person cannot end up under two
   * spellings. Saving is immediate: the select IS the decision, and a Save button beside
   * a dropdown invites somebody to change it and walk away.
   */
  const handleRemapAlias = async (rawValue: string, choice: string, isHouse: boolean) => {
    setError('')
    try {
      const isUser = choice.startsWith('005')
      await saveAlias.mutateAsync({
        raw_value: rawValue,
        rep_user_id: isUser ? choice : null,
        rep_label: isUser ? undefined : choice,
        is_house: isHouse,
      })
      const what = `"${rawValue}" now maps to ${isUser ? (userList.find((u) => u.id === choice)?.name ?? 'that person') : choice}${isHouse ? ', as a house account' : ''}.`
      setAnnouncement(what)
      toast({ title: 'Rep mapping updated', description: what })
    } catch (err) {
      setError(explainError(err, 'Could not update that rep mapping.'))
    }
  }

  /**
   * Add a sales account mapping before QuickBooks has ever sent that account.
   *
   * Jason's second review: "add an Add Product + Mapping function similar to Add Rep."
   * New accounts already appear on their own after an upload; this is for setting one up
   * in advance, so the first month it sells it is already categorised rather than
   * landing in the uncategorised queue.
   */
  const handleAddAccount = async () => {
    setError('')
    const account = newAccount.trim()
    if (!account) {
      setError('Type the sales account name exactly as QuickBooks sends it.')
      return
    }
    try {
      await saveCategory.mutateAsync({
        sales_account: account,
        category: newAccountCategory,
        product_group_label: newAccountLabel.trim() || undefined,
      })
      setNewAccount('')
      setNewAccountLabel('')
      const what = `"${account}" will count as ${newAccountCategory.replace('_', ' ')} from its first sale.`
      setAnnouncement(what)
      toast({ title: 'Product mapping added', description: what })
    } catch (err) {
      setError(explainError(err, 'Could not add that product mapping.'))
    }
  }

  /**
   * Rename the group a sales account reports under.
   *
   * This is the label By Product actually groups by, so changing it moves the account
   * between rows on that page - including for months already reported, because grouping
   * is resolved at read time. Worth knowing before renaming something mid-year.
   */
  const handleSaveLabel = async (salesAccount: string, category: string) => {
    const label = (labelEdits[salesAccount] ?? '').trim()
    setError('')
    try {
      await saveCategory.mutateAsync({
        sales_account: salesAccount,
        category,
        product_group_label: label || undefined,
      })
      setLabelEdits((p) => {
        const next = { ...p }
        delete next[salesAccount]
        return next
      })
      const what = `"${salesAccount}" now reports under ${label || salesAccount}.`
      setAnnouncement(what)
      toast({ title: 'Product mapping updated', description: what })
    } catch (err) {
      setError(explainError(err, 'Could not update that product mapping.'))
    }
  }

  const handleAddRep = async () => {
    setError('')
    const name = newRepName.trim()
    if (!name && !newRepUser) {
      setError('Choose a CRM user, or type a name, before adding a rep.')
      return
    }
    try {
      const chosen = userList.find((u) => u.id === newRepUser)
      await addRep.mutateAsync({
        rep_label: chosen?.name ?? name,
        rep_user_id: newRepUser || null,
      })
      setNewRepName('')
      setNewRepUser('')
      const what = `${chosen?.name ?? name} can now be given a target, before their first sale lands.`
      setAnnouncement(what)
      toast({ title: 'Rep added', description: what })
    } catch (err) {
      setError(explainError(err, 'Could not add that rep.'))
    }
  }

  return (
    <section className="p-6 space-y-6" aria-labelledby="sales-trackers-heading">
      <div>
        <h1 id="sales-trackers-heading" className="text-2xl font-semibold">
          Billings attribution
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Decisions the CRM cannot make for itself, and the lists behind the reporting: which
          products count as what, and who counts as a rep. Nothing is guessed.
        </p>
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {announcement ||
          (attribution.isFetching ? 'Loading the attribution queue' : data ? 'Queue loaded' : '')}
      </p>

      {error && (
        <Alert variant="destructive">
          <AlertDescription role="alert">{error}</AlertDescription>
        </Alert>
      )}

      {attribution.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load the queue</AlertTitle>
          <AlertDescription>
            {explainError(attribution.error, 'Something went wrong loading the attribution queue.')}
          </AlertDescription>
        </Alert>
      )}

      {attribution.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {data && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Unrecognised rep names ({data.unresolved_reps.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {data.unresolved_reps.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Every rep name in the data maps to somebody.
                </p>
              ) : (
                <ul className="space-y-4">
                  {data.unresolved_reps.map((r) => (
                    <li key={r.raw_value} className="border-b pb-4 last:border-0 last:pb-0">
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="min-w-[14rem]">
                          <p className="font-medium">{r.raw_value}</p>
                          <p className="text-xs text-muted-foreground">
                            {r.line_count.toLocaleString()} lines, {money(r.amount)} ·{' '}
                            {r.first_seen} to {r.last_seen}
                          </p>
                        </div>
                        <div>
                          <Label htmlFor={`rep-${r.raw_value}`} className="text-xs">
                            Attribute to
                          </Label>
                          <select
                            id={`rep-${r.raw_value}`}
                            className="mt-1 block h-9 rounded-md border border-input bg-background px-3 text-sm"
                            value={repChoice[r.raw_value] ?? ''}
                            onChange={(e) =>
                              setRepChoice((p) => ({ ...p, [r.raw_value]: e.target.value }))
                            }
                          >
                            <option value="">Choose…</option>
                            <optgroup label="People">
                              {userList.map((u) => (
                                <option key={u.id} value={u.id}>
                                  {u.name}
                                </option>
                              ))}
                            </optgroup>
                            <optgroup label="Not a person">
                              <option value="Unassigned">Unassigned</option>
                              <option value="GrackleDocs">GrackleDocs</option>
                            </optgroup>
                          </select>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleSaveRep(r.raw_value)}
                          disabled={!repChoice[r.raw_value] || saveAlias.isPending}
                          aria-label={`Save the mapping for ${r.raw_value}`}
                        >
                          Save
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                Uncategorised sales accounts ({data.unclassified_accounts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.unclassified_accounts.length === 0 ? (
                <p className="text-sm text-muted-foreground">Every sales account has a category.</p>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground mb-4">
                    These count toward the total but appear in neither Software nor Service, so the
                    split does not add up to the total until they are categorised.
                  </p>
                  <ul className="space-y-4">
                    {data.unclassified_accounts.map((a) => (
                      <li key={a.sales_account} className="border-b pb-4 last:border-0 last:pb-0">
                        <div className="flex flex-wrap items-end gap-3">
                          <div className="min-w-[14rem]">
                            <p className="font-medium">{a.sales_account}</p>
                            <p className="text-xs text-muted-foreground">
                              {a.line_count.toLocaleString()} lines, {money(a.amount)}
                            </p>
                          </div>
                          <div>
                            <Label htmlFor={`cat-${a.sales_account}`} className="text-xs">
                              Counts as
                            </Label>
                            <select
                              id={`cat-${a.sales_account}`}
                              className="mt-1 block h-9 rounded-md border border-input bg-background px-3 text-sm"
                              value={catChoice[a.sales_account] ?? ''}
                              onChange={(e) =>
                                setCatChoice((p) => ({ ...p, [a.sales_account]: e.target.value }))
                              }
                            >
                              <option value="">Choose…</option>
                              {CATEGORIES.map((c) => (
                                <option key={c.value} value={c.value}>
                                  {c.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleSaveCategory(a.sales_account)}
                            disabled={!catChoice[a.sales_account] || saveCategory.isPending}
                            aria-label={`Save the category for ${a.sales_account}`}
                          >
                            Save
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </CardContent>
          </Card>

          {/* ---------------------------------------------------- management --- */}

          <Card>
            <CardHeader>
              <CardTitle>Sales accounts ({data.categories.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Every sales account QuickBooks has sent, and what it counts as. New ones appear here
                automatically after an upload. Retiring one takes it out of the lists and stops it
                being asked about; its past sales keep counting in every total, so no figure on any
                other page changes.
              </p>
              <div className="flex flex-wrap items-end gap-3 mb-6 pb-6 border-b">
                <div>
                  <Label htmlFor="new-account" className="text-xs">
                    Add a sales account
                  </Label>
                  <Input
                    id="new-account"
                    className="mt-1 h-9 min-w-[16rem]"
                    value={newAccount}
                    onChange={(e) => setNewAccount(e.target.value)}
                    placeholder="Exactly as QuickBooks sends it"
                    aria-describedby="new-account-help"
                  />
                  <p id="new-account-help" className="text-xs text-muted-foreground mt-1">
                    Only needed to set one up in advance. Accounts appear here on their own after an
                    upload.
                  </p>
                </div>
                <div>
                  <Label htmlFor="new-account-category" className="text-xs">
                    Counts as
                  </Label>
                  <select
                    id="new-account-category"
                    className="mt-1 block h-9 rounded-md border border-input bg-background px-3 text-sm"
                    value={newAccountCategory}
                    onChange={(e) => setNewAccountCategory(e.target.value)}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="new-account-label" className="text-xs">
                    Report under (optional)
                  </Label>
                  <Input
                    id="new-account-label"
                    className="mt-1 h-9 min-w-[12rem]"
                    value={newAccountLabel}
                    onChange={(e) => setNewAccountLabel(e.target.value)}
                    placeholder="Defaults to its own name"
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddAccount}
                  disabled={saveCategory.isPending || !newAccount.trim()}
                >
                  Add product
                </Button>
              </div>

              <div className="overflow-x-auto" ref={accounts.ref} {...accounts.regionProps}>
                <table className="w-full text-sm">
                  <caption className="sr-only">
                    Sales accounts, the group they report under, what they count as, what they
                    billed, and whether they are still sold
                  </caption>
                  <thead>
                    <tr className="border-b">
                      <th scope="col" className="text-left font-medium py-2 pr-4">
                        Sales account
                      </th>
                      <th scope="col" className="text-left font-medium py-2 pr-4">
                        Reports under
                      </th>
                      <th scope="col" className="text-left font-medium py-2 pr-4">
                        Counts as
                      </th>
                      <th scope="col" className="text-right font-medium py-2 pr-4">
                        Billed
                      </th>
                      <th scope="col" className="text-left font-medium py-2 pr-4">
                        Last sold
                      </th>
                      <th scope="col" className="text-left font-medium py-2">
                        Still sold
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.categories.map((c) => (
                      <tr
                        key={c.id}
                        className={`border-b last:border-0 ${c.is_active ? '' : 'opacity-60'}`}
                      >
                        <th scope="row" className="text-left font-normal py-1.5 pr-4">
                          {c.sales_account}
                          {/* Stated in words, never by the dimming alone. */}
                          {!c.is_active && (
                            <span className="ml-1.5 text-xs text-muted-foreground">Retired</span>
                          )}
                        </th>
                        <td className="py-1.5 pr-4">
                          {/* The label By Product groups by. Editing it moves this account
                              between rows there, including for months already reported,
                              because grouping is resolved at read time. Saved on blur or
                              Enter rather than per keystroke. */}
                          <Input
                            className="h-8 min-w-[11rem]"
                            value={labelEdits[c.sales_account] ?? c.product_group_label ?? ''}
                            onChange={(e) =>
                              setLabelEdits((p) => ({ ...p, [c.sales_account]: e.target.value }))
                            }
                            onBlur={() =>
                              labelEdits[c.sales_account] !== undefined &&
                              labelEdits[c.sales_account] !== (c.product_group_label ?? '') &&
                              handleSaveLabel(c.sales_account, c.category)
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.currentTarget.blur()
                            }}
                            aria-label={`Group ${c.sales_account} reports under`}
                            disabled={saveCategory.isPending}
                          />
                        </td>
                        <td className="py-1.5 pr-4">
                          <select
                            className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                            value={c.category}
                            onChange={(e) => {
                              setCatChoice((p) => ({ ...p, [c.sales_account]: e.target.value }))
                              void handleSaveCategory(c.sales_account, e.target.value)
                            }}
                            aria-label={`Category for ${c.sales_account}`}
                            disabled={saveCategory.isPending}
                          >
                            {CATEGORIES.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-1.5 pr-4 text-right tabular-nums">
                          {money(c.amount)}
                          <span className="block text-xs text-muted-foreground">
                            {c.line_count.toLocaleString()} lines
                          </span>
                        </td>
                        <td className="py-1.5 pr-4 text-muted-foreground">
                          {c.last_seen ? c.last_seen.slice(0, 10) : 'never'}
                        </td>
                        <td className="py-1.5">
                          <Switch
                            checked={c.is_active}
                            onCheckedChange={(v) => handleCategoryActive(c.sales_account, v)}
                            aria-label={`${c.sales_account} is still sold`}
                            disabled={setCategoryActive.isPending}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Reps ({data.reps.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Who appears in the rep list and can be given a target. Anyone who shows up in an
                upload is added automatically; add someone here to set their target before their
                first sale. Retiring a rep keeps every line they ever sold counting.
              </p>

              <div className="flex flex-wrap items-end gap-3 mb-6 pb-6 border-b">
                <div>
                  <Label htmlFor="new-rep-user" className="text-xs">
                    Add a CRM user
                  </Label>
                  <select
                    id="new-rep-user"
                    className="mt-1 block h-9 min-w-[14rem] rounded-md border border-input bg-background px-3 text-sm"
                    value={newRepUser}
                    onChange={(e) => setNewRepUser(e.target.value)}
                  >
                    <option value="">Choose…</option>
                    {userList.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="new-rep-name" className="text-xs">
                    Or a name, if they have no CRM login
                  </Label>
                  <Input
                    id="new-rep-name"
                    className="mt-1 h-9 min-w-[14rem]"
                    value={newRepName}
                    onChange={(e) => setNewRepName(e.target.value)}
                    placeholder="Full name"
                    aria-describedby="new-rep-help"
                  />
                  <p id="new-rep-help" className="text-xs text-muted-foreground mt-1">
                    A CRM user is better: a target linked to a login survives a rename.
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddRep}
                  disabled={addRep.isPending || (!newRepUser && !newRepName.trim())}
                >
                  Add rep
                </Button>
              </div>

              <div className="overflow-x-auto" ref={repList.ref} {...repList.regionProps}>
                <table className="w-full text-sm">
                  <caption className="sr-only">
                    Reps, what they billed this year, and whether they still appear in reporting
                  </caption>
                  <thead>
                    <tr className="border-b">
                      <th scope="col" className="text-left font-medium py-2 pr-4">
                        Rep
                      </th>
                      <th scope="col" className="text-right font-medium py-2 pr-4">
                        Billed
                      </th>
                      <th scope="col" className="text-left font-medium py-2 pr-4">
                        Can hold a target
                      </th>
                      <th scope="col" className="text-left font-medium py-2">
                        In reporting
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.reps.map((r) => (
                      <tr
                        key={r.rep_label}
                        className={`border-b last:border-0 ${r.is_active ? '' : 'opacity-60'}`}
                      >
                        <th scope="row" className="text-left font-normal py-1.5 pr-4">
                          {r.display}
                          {!r.is_active && (
                            <span className="ml-1.5 text-xs text-muted-foreground">Retired</span>
                          )}
                          {!r.in_data && (
                            <span className="block text-xs text-muted-foreground">
                              no billings this year
                            </span>
                          )}
                        </th>
                        <td className="py-1.5 pr-4 text-right tabular-nums">
                          {money(r.amount)}
                          <span className="block text-xs text-muted-foreground">
                            {r.line_count.toLocaleString()} lines
                          </span>
                        </td>
                        <td className="py-1.5 pr-4 text-muted-foreground">
                          {r.can_hold_target ? 'Yes' : 'Not a person'}
                        </td>
                        <td className="py-1.5">
                          <Switch
                            checked={r.is_active}
                            onCheckedChange={(v) => handleRepActive(r.rep_label, r.roster_id, v)}
                            aria-label={`${r.display} appears in reporting`}
                            disabled={setRepActive.isPending}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Existing rep mappings ({data.aliases.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto" ref={queue.ref} {...queue.regionProps}>
                <table className="w-full text-sm">
                  <caption className="sr-only">
                    QuickBooks rep spellings and who they are attributed to
                  </caption>
                  <thead>
                    <tr className="border-b">
                      <th scope="col" className="text-left font-medium py-2 pr-4">
                        In QuickBooks
                      </th>
                      <th scope="col" className="text-left font-medium py-2 pr-4">
                        Attributed to
                      </th>
                      <th scope="col" className="text-left font-medium py-2">
                        House
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.aliases.map((a) => (
                      <tr key={a.id} className="border-b last:border-0">
                        <th scope="row" className="text-left font-normal py-1.5 pr-4">
                          <code className="text-xs">{a.raw_value}</code>
                        </th>
                        <td className="py-1.5 pr-4">
                          {/* Changing this re-points every line carrying that spelling,
                              including past ones, because attribution is resolved at
                              read time rather than stamped on the row. */}
                          <select
                            className="h-8 min-w-[13rem] rounded-md border border-input bg-background px-2 text-sm"
                            value={a.rep_user_id ?? a.rep_label}
                            onChange={(e) =>
                              handleRemapAlias(a.raw_value, e.target.value, a.is_house)
                            }
                            aria-label={`Who "${a.raw_value}" is attributed to`}
                            disabled={saveAlias.isPending}
                          >
                            <optgroup label="People">
                              {userList.map((u) => (
                                <option key={u.id} value={u.id}>
                                  {u.name}
                                </option>
                              ))}
                            </optgroup>
                            <optgroup label="Not a person">
                              <option value="Unassigned">Unassigned</option>
                              <option value="GrackleDocs">GrackleDocs</option>
                            </optgroup>
                            {!a.is_linked &&
                              !['Unassigned', 'GrackleDocs'].includes(a.rep_label) && (
                                <optgroup label="Current">
                                  <option value={a.rep_label}>
                                    {a.rep_label} (not a CRM user)
                                  </option>
                                </optgroup>
                              )}
                          </select>
                        </td>
                        <td className="py-1.5">
                          <Switch
                            checked={a.is_house}
                            onCheckedChange={(v) =>
                              handleRemapAlias(a.raw_value, a.rep_user_id ?? a.rep_label, v)
                            }
                            aria-label={`"${a.raw_value}" is a house account`}
                            disabled={saveAlias.isPending}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </section>
  )
}
